import React, { useRef, useState, useEffect } from 'react';
import { useLoader, useThree } from '@react-three/fiber';
import { TransformControls, Line } from '@react-three/drei';
import * as THREE from 'three';
import { useSceneStore } from '../../state/sceneStore';
import { useZScale } from './SceneCanvas';
import type { TransformControls as TransformControlsImpl } from 'three-stdlib';
import type { Obstacle, Sensor } from '../../api/models';

interface ReferenceImagePlaneProps {
  imageUrl: string;
  onDimensionsLoaded?: (width: number, height: number) => void;
}

type ImageDragBaseline = {
  sensors: Sensor[];
  obstacles: Obstacle[];
  pivotStart: [number, number];
  scale: number;
};

const ReferenceImagePlane: React.FC<ReferenceImagePlaneProps> = ({
  imageUrl,
  onDimensionsLoaded,
}) => {
  const texture = useLoader(THREE.TextureLoader, imageUrl);
  const {
    referenceImageConfig,
    setReferenceImageConfig,
    selectedObjectId,
    setSelectedObjectId,
    setIsDragging,
    referenceImageAlignMode,
    referenceImageScaleSensors,
    referenceImageScaleObstacles,
    applyReferenceImageScaleToScene,
    pointPickingStep,
    setPointPickingStep,
    pickingState,
    setPickingState,
  } = useSceneStore();
  const zScale = useZScale();
  const { gl } = useThree();

  useEffect(() => {
    if (texture) {
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.anisotropy = gl.capabilities.getMaxAnisotropy();
      texture.needsUpdate = true;
    }
  }, [texture, gl]);

  useEffect(() => {
    if (texture?.image && onDimensionsLoaded) {
      const { width, height } = texture.image as { width: number; height: number };
      if (width > 0 && height > 0) {
        onDimensionsLoaded(width, height);
      }
    }
  }, [texture, onDimensionsLoaded]);

  const aspect = texture.image ? texture.image.width / texture.image.height : 1;
  const planeWidth = 20;
  const planeHeight = planeWidth / aspect;

  let position: [number, number, number] = [0, 0, 0.02 * zScale];
  let rotation: [number, number, number] = [0, 0, 0];
  let scale: [number, number, number] = [1, 1, 1];

  if (referenceImageConfig.transform) {
    const t = referenceImageConfig.transform;
    position = [t.position[0], t.position[1], 0.02 * zScale];
    rotation = t.rotation;
    scale = t.scale;
  } else if (referenceImageConfig.origin && texture.image) {
    const { width: imgWidth, height: imgHeight } = texture.image;

    if (
      referenceImageConfig.scalePointA &&
      referenceImageConfig.scalePointB &&
      referenceImageConfig.scaleDistance &&
      referenceImageConfig.scaleDistance > 0
    ) {
      const [ax, ay] = referenceImageConfig.scalePointA;
      const [bx, by] = referenceImageConfig.scalePointB;
      const pixelDistance = Math.sqrt((ax - bx) ** 2 + (ay - by) ** 2);

      if (pixelDistance > 0) {
        const metersPerPixel = referenceImageConfig.scaleDistance / pixelDistance;
        const planeWidthInMeters = imgWidth * metersPerPixel;
        const scaleFactor = planeWidthInMeters / planeWidth;
        scale = [scaleFactor, scaleFactor, 1];
      }
    }

    const [originX, originY] = referenceImageConfig.origin;

    let angle = 0;
    if (referenceImageConfig.xAxisEnd) {
      const [xAxisEndX, xAxisEndY] = referenceImageConfig.xAxisEnd;
      const dx = xAxisEndX - originX;
      const dy = xAxisEndY - originY;
      angle = -Math.atan2(-dy, dx);
    }
    rotation = [0, 0, angle];

    const origin3DX = (originX / imgWidth - 0.5) * planeWidth;
    const origin3DY = (0.5 - originY / imgHeight) * planeHeight;

    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const rotatedOriginX = origin3DX * cos - origin3DY * sin;
    const rotatedOriginY = origin3DX * sin + origin3DY * cos;

    position = [-rotatedOriginX * scale[0], -rotatedOriginY * scale[1], 0.02 * zScale];
  }

  const isSelected = selectedObjectId === 'reference-image';
  const controlsRef = useRef<TransformControlsImpl>(null);
  const [target, setTarget] = useState<THREE.Mesh | null>(null);
  const scaleDragBaselineRef = useRef<ImageDragBaseline | null>(null);
  
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) {
      return undefined;
    }

    const handleDraggingChanged = (event: { value: boolean }) => {
      const dragging = event.value;
      setIsDragging(dragging);

      if (
        dragging &&
        (referenceImageAlignMode === 'scale' || referenceImageAlignMode === 'translate') &&
        (referenceImageScaleSensors || referenceImageScaleObstacles) &&
        target
      ) {
        const sceneState = useSceneStore.getState();
        scaleDragBaselineRef.current = {
          sensors: sceneState.sensors,
          obstacles: sceneState.obstacles,
          pivotStart: [target.position.x, target.position.y],
          scale: target.scale.x,
        };
      } else if (!dragging) {
        scaleDragBaselineRef.current = null;
      }

      if (dragging) {
        const canvas = document.querySelector('canvas');
        if (canvas) {
          canvas.style.pointerEvents = 'none';
        }

        const orbitControls = document.querySelector('canvas')?.parentElement?.querySelector('[data-orbit-controls]');
        if (orbitControls) {
          (orbitControls as any).enabled = false;
        }
      } else {
        const canvas = document.querySelector('canvas');
        if (canvas) {
          canvas.style.pointerEvents = 'auto';
        }
      }
    };

    (controls as unknown as { addEventListener: (name: string, cb: (event: { value: boolean }) => void) => void })
      .addEventListener('dragging-changed', handleDraggingChanged);
    return () => {
      (controls as unknown as { removeEventListener: (name: string, cb: (event: { value: boolean }) => void) => void })
        .removeEventListener('dragging-changed', handleDraggingChanged);
    };
  }, [
    target,
    setIsDragging,
    isSelected,
    referenceImageAlignMode,
    referenceImageScaleSensors,
    referenceImageScaleObstacles,
  ]);

  const applySceneTransformFromDrag = (currentPosition: [number, number], currentScale: number) => {
    const baseline = scaleDragBaselineRef.current;
    if (!baseline || baseline.scale <= 0) {
      return;
    }

    const scaleFactor = referenceImageAlignMode === 'scale'
      ? currentScale / baseline.scale
      : 1;

    const positionUnchanged =
      Math.abs(currentPosition[0] - baseline.pivotStart[0]) < 1e-9
      && Math.abs(currentPosition[1] - baseline.pivotStart[1]) < 1e-9;
    if (referenceImageAlignMode === 'translate' && positionUnchanged) {
      return;
    }
    if (referenceImageAlignMode === 'scale' && positionUnchanged && Math.abs(scaleFactor - 1) < 1e-9) {
      return;
    }

    applyReferenceImageScaleToScene(
      baseline.sensors,
      baseline.obstacles,
      baseline.pivotStart,
      currentPosition,
      scaleFactor,
      {
        scaleSensors: referenceImageScaleSensors,
        scaleObstacles: referenceImageScaleObstacles,
      },
    );
  };
  
  const handlePlaneClick = (e: any) => {
    if (referenceImageAlignMode) {
      e.stopPropagation();
      setSelectedObjectId('reference-image');
    } else if (pointPickingStep) {
      e.stopPropagation();
      
      const pt = e.intersections[0].point;

      if (pointPickingStep === 'origin') {
        setPickingState({ origin: [pt.x, pt.y] });
        setPointPickingStep('xaxis');
      } else if (pointPickingStep === 'xaxis') {
        setPickingState({ xAxisEnd: [pt.x, pt.y] });
        setPointPickingStep('scale-1');
      } else if (pointPickingStep === 'scale-1') {
        setPickingState({ scalePointA: [pt.x, pt.y] });
        setPointPickingStep('scale-2');
      } else if (pointPickingStep === 'scale-2') {
        setPickingState({ scalePointB: [pt.x, pt.y] });
        setPointPickingStep('scale-distance');
      }
    }
  };

  return (
    <>
      <mesh 
        ref={setTarget}
        position={position} 
        rotation={new THREE.Euler(...rotation)} 
        scale={scale}
        onClick={handlePlaneClick}
      >
        <planeGeometry args={[planeWidth, planeHeight]} />
        <meshBasicMaterial map={texture} side={THREE.DoubleSide} transparent opacity={isSelected ? 1.0 : 0.7} />
      </mesh>

      {pickingState.origin && (
        <mesh position={[pickingState.origin[0], pickingState.origin[1], 0.03 * zScale]}>
          <circleGeometry args={[0.4, 32]} />
          <meshBasicMaterial color="#3b82f6" depthTest={false} transparent opacity={0.8} />
        </mesh>
      )}

      {pickingState.origin && pickingState.xAxisEnd && (
        <>
          <Line 
            points={[
              [pickingState.origin[0], pickingState.origin[1], 0.03 * zScale], 
              [pickingState.xAxisEnd[0], pickingState.xAxisEnd[1], 0.03 * zScale]
            ]} 
            color="#ffffff" 
            lineWidth={8}
          />
          <Line 
            points={[
              [pickingState.origin[0], pickingState.origin[1], 0.031 * zScale], 
              [pickingState.xAxisEnd[0], pickingState.xAxisEnd[1], 0.031 * zScale]
            ]} 
            color="#3b82f6" 
            lineWidth={4}
          />
          <mesh position={[pickingState.xAxisEnd[0], pickingState.xAxisEnd[1], 0.031 * zScale]}>
            <circleGeometry args={[0.3, 32]} />
            <meshBasicMaterial color="#3b82f6" depthTest={false} transparent opacity={0.8} />
          </mesh>
        </>
      )}

      {pickingState.scalePointA && (
        <mesh position={[pickingState.scalePointA[0], pickingState.scalePointA[1], 0.03 * zScale]}>
          <circleGeometry args={[0.4, 32]} />
          <meshBasicMaterial color="#10b981" depthTest={false} transparent opacity={0.8} />
        </mesh>
      )}

      {pickingState.scalePointB && (
        <>
          <mesh position={[pickingState.scalePointB[0], pickingState.scalePointB[1], 0.03 * zScale]}>
            <circleGeometry args={[0.4, 32]} />
            <meshBasicMaterial color="#10b981" depthTest={false} transparent opacity={0.8} />
          </mesh>
          <Line 
            points={[
              [pickingState.scalePointA![0], pickingState.scalePointA![1], 0.03 * zScale],
              [pickingState.scalePointB[0], pickingState.scalePointB[1], 0.03 * zScale]
            ]} 
            color="#ffffff" 
            lineWidth={8}
          />
          <Line 
            points={[
              [pickingState.scalePointA![0], pickingState.scalePointA![1], 0.031 * zScale],
              [pickingState.scalePointB[0], pickingState.scalePointB[1], 0.031 * zScale]
            ]} 
            color="#10b981" 
            lineWidth={4}
          />
        </>
      )}

      {isSelected && target && (
        <TransformControls
          object={target}
          ref={controlsRef}
          mode={referenceImageAlignMode || 'translate'}
          showX={referenceImageAlignMode === 'translate' || referenceImageAlignMode === 'scale'}
          showY={referenceImageAlignMode === 'translate' || referenceImageAlignMode === 'scale'}
          showZ={referenceImageAlignMode === 'rotate' || referenceImageAlignMode === 'scale'}
          onChange={() => {
            if (target) {
              const controls = controlsRef.current as any;
              
              // Unconditionally constrain the 3D object to prevent any visual glitches
              if (referenceImageAlignMode === 'scale') {
                if (controls && controls.axis === 'Y') {
                  target.scale.x = target.scale.y;
                } else if (controls && controls.axis === 'X') {
                  target.scale.y = target.scale.x;
                } else {
                  target.scale.y = target.scale.x;
                }
                target.scale.z = 1; // Z scale doesn't matter for a plane, keep it 1
              } else if (referenceImageAlignMode === 'translate') {
                // Constrain translation to X and Y only (keep Z constant)
                target.position.z = position[2];
              } else if (referenceImageAlignMode === 'rotate') {
                // Constrain rotation to Z only
                target.rotation.x = rotation[0];
                target.rotation.y = rotation[1];
              }
              
              // Save to the store only if actively dragging (using synchronous controls flag)
              // This avoids asynchronous React state bugs and infinite loop issues
              if (controls && controls.dragging) {
                const { position: tPos, rotation: tRot, scale: tScale } = target;
                setReferenceImageConfig({
                  transform: {
                    position: [tPos.x, tPos.y, tPos.z],
                    rotation: [tRot.x, tRot.y, tRot.z],
                    scale: [tScale.x, tScale.y, tScale.z],
                  }
                });

                if (referenceImageAlignMode === 'scale' || referenceImageAlignMode === 'translate') {
                  applySceneTransformFromDrag([tPos.x, tPos.y], tScale.x);
                }
              }
            }
          }}
        />
      )}
    </>
  );
};

export default ReferenceImagePlane;
